import { Card, Drawer, Layout, Menu, theme } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppBreakpoint } from '@/contexts/BreakpointContext';
import { useThemeMode } from '@/contexts/ThemeContext';
import { useSyncedPageTabs } from '@/hooks/useSyncedPageTabs';
import { UserWatermark } from '@/hooks/useWatermark';
import AppHeader from './AppHeader';
import AppSider from './AppSider';
import KeepAliveOutlet from './KeepAliveOutlet';
import './moduleMenuAccent.less';
import { isMainLayoutFixedTableScrollPath } from './mainFixedTableScrollPaths';
import PageTabs from './PageTabs';
import SearchModal from './SearchModal';
import { useEnableTableScrollYLayout } from './tableScrollLayout';
import { useEffectiveMenuItems } from './useEffectiveMenuItems';

const { Content } = Layout;
const LS_COLLAPSED = 'sider_collapsed';

/** 顶栏 / 标签区占高不收缩，仅剩高度给 `<main>` 做纵向滚动容器 */
const mainLayoutBranchNoShrinkStyle = { flexShrink: 0 };

/** 右侧列锁视口高度 + 纵向 flex，保证仅 `main.main-container`（flex:1 min-h:0 overflow-auto）滚动 */
const mainLayoutRightColumnStyle = {
    minWidth: 0,
    minHeight: 0,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
};

/** 浅色模式下右侧主内容列背景 */
const mainLayoutRightColumnLightBackground = 'linear-gradient(155deg, #fbfcfe 35%, #f0f3fa 71%, #f5f1fe 91%)';

function safeLsSet(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch {
        // quota exceeded or private-browsing mode
    }
}

function getRouteOpenKeys(pathname) {
    if (pathname === '/') return [];
    if (pathname.startsWith('/medproject')) return ['medproject'];
    if (pathname.startsWith('/invitation')) return ['invitation'];
    if (pathname.startsWith('/conf')) return ['conf'];
    if (pathname.startsWith('/selection')) return ['selection'];
    if (pathname.startsWith('/wj')) return ['wj'];
    if (pathname.startsWith('/healthclass')) return ['healthclass'];
    if (pathname.startsWith('/kb')) return ['kb'];
    if (pathname.startsWith('/healthaccount')) return ['healthaccount'];
    if (pathname.startsWith('/enterprise') || pathname.startsWith('/my')) return ['enterprise'];
    if (pathname.startsWith('/uc')) return ['uc'];
    if (pathname.startsWith('/operation')) return ['operation'];
    if (pathname.startsWith('/finance')) return ['finance'];
    if (pathname.startsWith('/dc')) return ['dc'];
    return [];
}

export default function MainLayout() {
    const { token } = theme.useToken();
    const { resolvedMode } = useThemeMode();
    const { isMobile } = useAppBreakpoint();
    const enableTableScrollYLayout = useEnableTableScrollYLayout();

    const navigate = useNavigate();
    const { pathname } = useLocation();

    const useFixedTableScrollChain = enableTableScrollYLayout && isMainLayoutFixedTableScrollPath(pathname);
    const mainContainerClassName = useFixedTableScrollChain ? 'main-container' : 'main-container main-container--natural-scroll';

    const { tabs, onTabsChange } = useSyncedPageTabs();

    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(LS_COLLAPSED) === 'true');
    const [searchOpen, setSearchOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const prevIsMobileRef = useRef(null);

    const openSearchModal = useCallback(() => setSearchOpen(true), []);
    const closeSearchModal = useCallback(() => setSearchOpen(false), []);

    const handleToggleSider = useCallback(() => {
        if (isMobile) {
            setMobileMenuOpen(true);
            return;
        }
        setCollapsed(prev => {
            const next = !prev;
            safeLsSet(LS_COLLAPSED, String(next));
            return next;
        });
    }, [isMobile]);

    useEffect(() => {
        const handler = e => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                openSearchModal();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [openSearchModal]);

    useEffect(() => {
        const prev = prevIsMobileRef.current;
        if (prev === null) {
            if (isMobile) setCollapsed(true);
        } else if (prev !== isMobile) {
            if (isMobile) {
                setCollapsed(true);
            } else {
                setCollapsed(localStorage.getItem(LS_COLLAPSED) === 'true');
                setMobileMenuOpen(false);
            }
        }
        prevIsMobileRef.current = isMobile;
    }, [isMobile]);

    const mobileMenuSelectedKeys = useMemo(() => {
        if (pathname.startsWith('/invitation')) {
            return ['invitation', pathname];
        }
        return [pathname];
    }, [pathname]);
    const mobileMenuDefaultOpenKeys = useMemo(() => getRouteOpenKeys(pathname), [pathname]);
    const handleMobileMenuClick = useCallback(
        ({ key }) => {
            navigate(key);
            setMobileMenuOpen(false);
        },
        [navigate],
    );

    // 移动端菜单与桌面端共用同一份有效菜单（含权限过滤和动态注入）
    const [mobileSubMenuList, setMobileSubMenuList] = useState(() => {
        try {
            const raw = localStorage.getItem('permissionsList');
            return raw ? JSON.parse(JSON.parse(raw).data) : [];
        } catch {
            return [];
        }
    });
    const [mobileHealthclassSubMenuList, setMobileHealthclassSubMenuList] = useState([]);
    const [mobileHealthclassOrgCount, setMobileHealthclassOrgCount] = useState(0);
    useEffect(() => {
        try {
            const raw = localStorage.getItem('permissionsList');
            if (raw) setMobileSubMenuList(JSON.parse(JSON.parse(raw).data));
        } catch {
            /* ignore */
        }
    }, [pathname]);
    const mobileEffectiveMenuItems = useEffectiveMenuItems({
        pathname,
        subMenuList: mobileSubMenuList,
        healthclassSubMenuList: mobileHealthclassSubMenuList,
        healthclassOrgCount: mobileHealthclassOrgCount,
    });

    const cardNaturalChainStyle = useFixedTableScrollChain
        ? {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
          }
        : {
              flex: 'none',
              minHeight: '100%',
              alignSelf: 'stretch',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
          };

    const rightColumnLayoutStyle = useMemo(
        () => ({
            ...mainLayoutRightColumnStyle,
            ...(resolvedMode === 'light' ? { background: mainLayoutRightColumnLightBackground } : {}),
        }),
        [resolvedMode],
    );

    const cardBodyNaturalChainStyle = useFixedTableScrollChain
        ? {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
          }
        : {
              flex: 'none',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              minHeight: 'auto',
          };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {!isMobile && <AppSider collapsed={collapsed} />}
            <Layout style={rightColumnLayoutStyle}>
                <div style={mainLayoutBranchNoShrinkStyle}>
                    <AppHeader siderCollapsed={collapsed} onToggleSider={handleToggleSider} onOpenSearch={openSearchModal} />
                </div>
                {!isMobile && (
                    <div style={mainLayoutBranchNoShrinkStyle}>
                        <PageTabs tabs={tabs} onTabsChange={onTabsChange} />
                    </div>
                )}
                <Content
                    component="main"
                    className={mainContainerClassName}
                    style={{
                        margin: token.margin,
                        padding: 0,
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        minWidth: 0,
                        minHeight: 0,
                        background: token.colorBgLayout,
                    }}
                >
                    <UserWatermark>
                        <Card
                            className="main-container-card"
                            data-table-scroll-layout={useFixedTableScrollChain ? 'fixed' : 'natural'}
                            style={cardNaturalChainStyle}
                            styles={{
                                body: cardBodyNaturalChainStyle,
                            }}
                        >
                            <KeepAliveOutlet aliveKeys={tabs.map(t => t.key)} />
                        </Card>
                    </UserWatermark>
                </Content>
            </Layout>

            <SearchModal open={searchOpen} onClose={closeSearchModal} />

            {isMobile && (
                <Drawer placement="left" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} width={240} closable={false} styles={{ header: { display: 'none' }, body: { padding: 0 } }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            height: 56,
                            padding: '0 20px',
                            borderBottom: `1px solid ${token.colorSplit}`,
                        }}
                    >
                        <img src="/logo.svg" alt="logo" style={{ width: 26, height: 26 }} />
                        <span style={{ fontWeight: 700, fontSize: 16, color: token.colorTextHeading }}>华媒康讯</span>
                    </div>
                    <Menu mode="inline" items={mobileEffectiveMenuItems} selectedKeys={mobileMenuSelectedKeys} defaultOpenKeys={mobileMenuDefaultOpenKeys} onClick={handleMobileMenuClick} style={{ borderRight: 'none', fontSize: 16 }} />
                </Drawer>
            )}
        </Layout>
    );
}
